/**
 * Plate Math Utilities
 *
 * Ensures all weights are rounded to realistic plate increments
 * (standard barbell plates come in 2.5, 5, 10, 25, 35, 45 lb increments)
 *
 * Config-aware. Each gym has different inventory — some have only
 * 5-lb minimum plates, some have 1-kg micro plates, women's bars are
 * 35 lb instead of 45. The user's PlateConfig drives both rounding
 * and the loading hint shown in V2's ARMED view.
 *
 * ML consumers (lib/statisticalRecommendation, lib/dynamic1RM, etc.)
 * call roundToNearestPlate with the user's config so suggested loads
 * always match what they can actually load on the bar.
 */

const STORAGE_KEY = 'plateConfig';

export interface PlateConfig {
  /** Bar weight in lb. 45 (men's), 35 (women's), 25 (technique), 15 (kid's). */
  barWeight: number;
  /** Available plate sizes (per side, in lb). Descending order. */
  plates: number[];
  /** Smallest increment of total weight the gym can produce — derived from
   *  smallest plate × 2 sides. Used by roundToNearestPlate when called
   *  with this config. */
  minIncrement: number;
}

export const DEFAULT_PLATE_CONFIG: PlateConfig = {
  barWeight: 45,
  plates: [45, 35, 25, 10, 5, 2.5],
  minIncrement: 5, // 2.5 lb × 2 sides
};

/** Common plate configurations the user can pick from in settings. */
export const PRESET_PLATE_CONFIGS: Array<{ name: string; config: PlateConfig }> = [
  {
    name: 'Standard gym (45 lb bar, 2.5–45 plates)',
    config: DEFAULT_PLATE_CONFIG,
  },
  {
    name: 'Women\'s bar (35 lb bar, 2.5–45 plates)',
    config: { barWeight: 35, plates: [45, 35, 25, 10, 5, 2.5], minIncrement: 5 },
  },
  {
    name: 'No 2.5\'s (45 lb bar, 5–45 plates)',
    config: { barWeight: 45, plates: [45, 35, 25, 10, 5], minIncrement: 10 },
  },
  {
    name: 'Powerlifting (45 lb bar, micro plates)',
    config: { barWeight: 45, plates: [45, 35, 25, 10, 5, 2.5, 1.25], minIncrement: 2.5 },
  },
  {
    name: 'Kilo plates (20 kg bar, 1.25–25 kg plates)',
    config: { barWeight: 44, plates: [55, 44, 33, 22, 11, 5.5, 2.75], minIncrement: 5.5 },
  },
];

export function loadPlateConfig(): PlateConfig {
  if (typeof window === 'undefined') return DEFAULT_PLATE_CONFIG;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_PLATE_CONFIG;
    const parsed = JSON.parse(raw);
    if (
      typeof parsed?.barWeight === 'number' &&
      Array.isArray(parsed?.plates) &&
      parsed.plates.length > 0
    ) {
      const smallest = Math.min(...parsed.plates);
      return {
        barWeight: parsed.barWeight,
        plates: [...parsed.plates].sort((a, b) => b - a),
        minIncrement: smallest * 2,
      };
    }
  } catch { /* ignore */ }
  return DEFAULT_PLATE_CONFIG;
}

export function savePlateConfig(config: PlateConfig): void {
  if (typeof window === 'undefined') return;
  try {
    const smallest = Math.min(...config.plates);
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      barWeight: config.barWeight,
      plates: [...config.plates].sort((a, b) => b - a),
      minIncrement: smallest * 2,
    }));
  } catch { /* ignore */ }
  // Update the active config so subsequent server-side / module
  // recommendation calls pick it up without having to be passed the
  // config explicitly. PlateLoading also calls this on Gym Setup
  // change in the modal.
  _activeConfig = config;
}

/**
 * Process-wide "current user plate config" — the fallback used when
 * roundToNearestPlate (and friends) are called without an explicit
 * config argument. Lets the 11 recommendation-engine call sites in
 * lib/dynamic1RM, lib/statisticalRecommendation, etc. respect the
 * user's gym setup without having to thread config through every
 * function signature.
 *
 * Lifecycle:
 *   - SSR / module load: undefined (engine falls back to 5-lb rounding)
 *   - Client mount: V2 calls setActivePlateConfig(loadPlateConfig())
 *   - User changes gym setup in Plate Calculator: savePlateConfig
 *     updates this in addition to writing localStorage.
 *
 * Important: don't read this in SSR code paths — the active config is
 * a client-only concept. Server-rendered placeholders should keep
 * using default rounding so the hydration matches.
 */
let _activeConfig: PlateConfig | undefined;

export function setActivePlateConfig(config: PlateConfig | undefined): void {
  _activeConfig = config;
}

export function getActivePlateConfig(): PlateConfig | undefined {
  return _activeConfig;
}

/**
 * Round weight to the nearest loadable increment.
 *
 * Resolution order:
 *   1. Explicit `config` argument (caller has a specific config)
 *   2. Active user config set via setActivePlateConfig (client mount)
 *   3. Default 5-lb increments (SSR / no-config fallback)
 *
 * So a call site that passes nothing — like the 11 recommendation-
 * engine consumers — picks up the user's gym setup automatically
 * once V2 has set it on mount.
 */
export function roundToNearestPlate(weight: number, config?: PlateConfig): number {
  if (weight <= 0) return 0;
  const inc = (config ?? _activeConfig)?.minIncrement ?? 5;
  return Math.round(weight / inc) * inc;
}

/**
 * Round weight UP to the nearest loadable increment.
 */
export function roundUpToNearestPlate(weight: number, config?: PlateConfig): number {
  if (weight <= 0) return 0;
  const inc = (config ?? _activeConfig)?.minIncrement ?? 5;
  return Math.ceil(weight / inc) * inc;
}

/**
 * Round weight DOWN to the nearest loadable increment.
 */
export function roundDownToNearestPlate(weight: number, config?: PlateConfig): number {
  if (weight <= 0) return 0;
  const inc = (config ?? _activeConfig)?.minIncrement ?? 5;
  return Math.floor(weight / inc) * inc;
}

/**
 * Calculate plates needed per side for a given total weight, given
 * the user's bar + plate inventory. Returns the greedy decomposition
 * — largest plates first — which is how every gym actually loads.
 */
export function calculatePlatesNeeded(totalWeight: number, config: PlateConfig = DEFAULT_PLATE_CONFIG): number[] {
  const weightPerSide = (totalWeight - config.barWeight) / 2;
  if (weightPerSide <= 0) return [];

  const plates: number[] = [];
  let remaining = weightPerSide;

  // Plates are stored in descending order.
  for (const plateSize of config.plates) {
    while (remaining >= plateSize - 0.001) {
      plates.push(plateSize);
      remaining -= plateSize;
    }
  }

  return plates;
}

/**
 * Format plate loading for inline display. e.g. "45 + 25 + 10 per side".
 * Returns null when the user is below bar weight so the caller can
 * decide what to show (often "Bar only" or hide entirely).
 */
export function formatPlateLoading(totalWeight: number, config: PlateConfig = DEFAULT_PLATE_CONFIG): string {
  const plates = calculatePlatesNeeded(totalWeight, config);
  if (plates.length === 0) {
    if (totalWeight > 0 && totalWeight < config.barWeight) {
      return `Below bar (${config.barWeight} lb)`;
    }
    return `Bar only (${config.barWeight} lb)`;
  }
  // Group identical plates for compactness: "45×2 + 25 + 10".
  const counts = new Map<number, number>();
  for (const p of plates) counts.set(p, (counts.get(p) ?? 0) + 1);
  const parts: string[] = [];
  counts.forEach((count, size) => {
    parts.push(count > 1 ? `${size}×${count}` : `${size}`);
  });
  return `${parts.join(' + ')} per side`;
}

/**
 * Detect when the target weight can't be loaded exactly with the
 * given plates — caller can show "snap to X or Y?" feedback.
 */
export function getLoadingResidual(totalWeight: number, config: PlateConfig = DEFAULT_PLATE_CONFIG): number {
  const plates = calculatePlatesNeeded(totalWeight, config);
  const loadedPerSide = plates.reduce((sum, p) => sum + p, 0);
  const loadedTotal = config.barWeight + loadedPerSide * 2;
  return totalWeight - loadedTotal;
}
